import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../hooks/useAppSelector';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import Toast from '../Toast';
import LoadingOverlay from '../LoadingOverlay';
import ConfirmDialog from '../ConfirmDialog';

const PERMISSIONS = [
    'VIEW_ORDERS',
    'UPDATE_ORDER_STATUS',
    'MANAGE_STAFF',
    'VIEW_ATTENDANCE',
    'VIEW_REPORTS',
    'COLLECT_FEEDBACK',
    'MANAGE_MENU',
    'MANAGE_INVENTORY',
    'VIEW_ANALYTICS',
    'MANAGE_TABLES',
    'PROCESS_PAYMENTS'
];

const ROLE_TEMPLATES = {
    COOK: ['VIEW_ORDERS', 'UPDATE_ORDER_STATUS'],
    WAITER: ['VIEW_ORDERS', 'UPDATE_ORDER_STATUS', 'MANAGE_TABLES'],
    MANAGER: ['VIEW_ORDERS', 'UPDATE_ORDER_STATUS', 'VIEW_ATTENDANCE', 'VIEW_REPORTS', 'COLLECT_FEEDBACK', 'VIEW_ANALYTICS'],
    CASHIER: ['VIEW_ORDERS', 'PROCESS_PAYMENTS', 'COLLECT_FEEDBACK'],
    SECURITY: ['VIEW_ATTENDANCE']
};

const RoleManagement = () => {
    const { user, restaurant } = useAppSelector(state => state.auth);
    const restaurantId = restaurant?._id || user?.id;
    
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [showModal, setShowModal] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [confirmDialog, setConfirmDialog] = useState({ 
        isOpen: false, 
        title: '', 
        message: '', 
        onConfirm: null,
        confirmText: 'Confirm',
        confirmColor: 'red'
    });

    const [formData, setFormData] = useState({
        name: '',
        permissions: []
    });

    useEffect(() => {
        if (restaurantId) {
            fetchRoles();
        }
    }, [restaurantId]);

    const fetchRoles = async () => {
        try {
            setLoading(true);
            const response = await axios.get(API_ENDPOINTS.ROLES_ALL(restaurantId));
            setRoles(response.data);
        } catch (error) {
            showToast('Failed to fetch roles', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePermissionToggle = (permission) => {
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.includes(permission)
                ? prev.permissions.filter(p => p !== permission)
                : [...prev.permissions, permission]
        }));
    };

    const handleTemplateSelect = (templateName) => {
        const template = ROLE_TEMPLATES[templateName];
        if (template) {
            setFormData(prev => ({ ...prev, permissions: template }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        setLoadingMessage(editingRole ? 'Updating role...' : 'Creating role...');
        try {
            if (editingRole) {
                await axios.put(API_ENDPOINTS.ROLE_UPDATE(editingRole._id), {
                    permissions: formData.permissions,
                    restaurantId
                });
                showToast('Role updated successfully');
            } else {
                await axios.post(API_ENDPOINTS.ROLE_CREATE, {
                    name: formData.name,
                    permissions: formData.permissions,
                    restaurantId
                });
                showToast('Role created successfully');
            }
            setShowModal(false);
            setEditingRole(null);
            resetForm();
            await fetchRoles();
        } catch (error) {
            showToast(error.response?.data?.message || 'Operation failed', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleEdit = (role) => {
        setEditingRole(role);
        setFormData({
            name: role.name,
            permissions: role.permissions || []
        });
        setShowModal(true);
    };

    const handleDelete = (id, name) => {
        setConfirmDialog({
            isOpen: true,
            title: 'Delete Role',
            message: `Are you sure you want to delete the role "${name}"? This action cannot be undone.`,
            confirmText: 'Delete',
            confirmColor: 'red',
            onConfirm: async () => {
                setActionLoading(true);
                setLoadingMessage('Deleting role...');
                try {
                    await axios.delete(API_ENDPOINTS.ROLE_DELETE(id));
                    showToast('Role deleted successfully');
                    await fetchRoles();
                } catch (error) {
                    showToast(error.response?.data?.message || 'Failed to delete role', 'error');
                } finally {
                    setActionLoading(false);
                }
            }
        });
    };

    const resetForm = () => {
        setFormData({
            name: '',
            permissions: []
        });
    };

    return (
        <div className="relative overflow-x-hidden min-w-0">
            {actionLoading && <LoadingOverlay message={loadingMessage} />}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                <h2 className="text-base font-bold text-stone-800 truncate">Roles & Permissions</h2>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={async () => {
                            await fetchRoles();
                            showToast('Roles list refreshed successfully');
                        }}
                        disabled={loading}
                        className="inline-flex items-center justify-center p-2 rounded-lg font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60 transition-colors shadow-sm size-9"
                        title="Refresh"
                        aria-label="Refresh"
                    >
                        {loading ? (
                            <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <span className="text-sm">↻</span>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => { resetForm(); setEditingRole(null); setShowModal(true); }}
                        className="h-9 px-3 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600"
                    >
                        + Add Role
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-400 border-t-transparent" />
                </div>
            ) : (
                <>
                    <div className="hidden md:block bg-white rounded-xl border border-stone-200 shadow-sm overflow-x-auto">
                        <table className="w-full min-w-[400px] text-xs">
                            <thead className="bg-stone-50">
                                <tr>
                                    <th className="px-3 py-2 text-left font-semibold text-stone-600">Role</th>
                                    <th className="px-3 py-2 text-left font-semibold text-stone-600">Permissions</th>
                                    <th className="px-3 py-2 text-left font-semibold text-stone-600">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {roles.map((role) => (
                                    <tr key={role._id} className="border-t border-stone-100 hover:bg-stone-50/50">
                                        <td className="px-3 py-2 font-medium text-stone-800">{role.name}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex flex-wrap gap-1">
                                                {role.permissions?.map((perm) => (
                                                    <span key={perm} className="px-1.5 py-0.5 bg-orange-50 text-orange-800 text-[10px] rounded border border-orange-100">{perm}</span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <button type="button" onClick={() => handleEdit(role)} className="text-orange-600 hover:text-orange-700 font-medium mr-2">Edit</button>
                                            <button type="button" onClick={() => handleDelete(role._id, role.name)} className="text-red-600 hover:text-red-700 font-medium">Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="md:hidden space-y-2">
                        {roles.length === 0 ? (
                            <div className="bg-white rounded-xl border border-stone-200 p-3 text-center text-stone-500 text-xs">No roles found</div>
                        ) : (
                            roles.map((role) => (
                                <div key={role._id} className="bg-white rounded-xl border border-stone-200 p-3 border-l-4 border-l-orange-400">
                                    <div className="flex justify-between items-start gap-2 mb-2">
                                        <h3 className="font-semibold text-stone-800 text-sm">{role.name}</h3>
                                        <div className="flex gap-1.5">
                                            <button type="button" onClick={() => handleEdit(role)} className="text-orange-600 text-xs font-medium">Edit</button>
                                            <button type="button" onClick={() => handleDelete(role._id, role.name)} className="text-red-600 text-xs font-medium">Delete</button>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {role.permissions?.map((perm) => (
                                            <span key={perm} className="px-1.5 py-0.5 bg-orange-50 text-orange-800 text-[10px] rounded">{perm}</span>
                                        ))}
                                        {(!role.permissions || role.permissions.length === 0) && <span className="text-stone-400 text-[10px]">No permissions</span>}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}

            {showModal && (
                <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4" onClick={() => { setShowModal(false); setEditingRole(null); resetForm(); }}>
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col border border-stone-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-b border-stone-200 bg-stone-50/80 flex-shrink-0">
                            <h3 className="text-base font-bold text-stone-800">{editingRole ? 'Edit Role' : 'Add Role'}</h3>
                            <button type="button" onClick={() => { setShowModal(false); setEditingRole(null); resetForm(); }} className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-200 text-lg leading-none">×</button>
                        </div>
                        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                            <div className="overflow-y-auto flex-1 p-3 sm:p-4 space-y-3">
                                {!editingRole && (
                                    <>
                                        <div>
                                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Role name *</p>
                                            <select name="name" value={formData.name} onChange={handleInputChange} required className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none bg-white">
                                                <option value="">Select role</option>
                                                <option value="COOK">Cook</option>
                                                <option value="WAITER">Waiter</option>
                                                <option value="MANAGER">Manager</option>
                                                <option value="CASHIER">Cashier</option>
                                                <option value="SECURITY">Security</option>
                                            </select>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Templates</p>
                                            <div className="flex flex-wrap gap-1.5">
                                                {Object.keys(ROLE_TEMPLATES).map(template => (
                                                    <button key={template} type="button" onClick={() => { setFormData(prev => ({ ...prev, name: template })); handleTemplateSelect(template); }} className="px-2 py-1 text-[10px] font-semibold rounded-lg bg-stone-100 text-stone-700 hover:bg-orange-100 hover:text-orange-800">
                                                        {template}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Permissions</p>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-48 overflow-y-auto border border-stone-200 rounded-lg p-2">
                                        {PERMISSIONS.map(permission => (
                                            <label key={permission} className="flex items-center gap-2 cursor-pointer text-xs">
                                                <input type="checkbox" checked={formData.permissions.includes(permission)} onChange={() => handlePermissionToggle(permission)} className="rounded border-stone-300 text-orange-500 focus:ring-orange-400" />
                                                <span className="text-stone-700">{permission}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 px-3 sm:px-4 py-2.5 border-t border-stone-200 bg-white flex-shrink-0">
                                <button type="button" onClick={() => { setShowModal(false); setEditingRole(null); resetForm(); }} className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50">Cancel</button>
                                <button type="submit" disabled={actionLoading} className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50">{editingRole ? 'Update' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
                confirmText={confirmDialog.confirmText}
                confirmColor={confirmDialog.confirmColor}
            />

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default RoleManagement;

